import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import Toast from "@/components/ui/toast";
import { apiClient } from "@/lib/apiClient";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import TabGroup from "@/components/ui/tab";
import ProgressBar from "@/components/ui/progress-bar";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Eye,
  Edit,
  Delete,
  Employees,
  Document,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type { Poll, PollStatus } from "../types/polls.types";
import { SurveyStatus } from "../types/polls.types";
import { pollsApi } from "../services/polls.api";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./PollsPage.module.scss";

interface AdminEducationItem {
  id: string;
  name?: string;
}

const PollsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.POLLS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.POLLS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.POLLS_DELETE);

  const [polls, setPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Poll[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [activeTab, setActiveTab] = useState(0);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [educationOptions, setEducationOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [deleteModalPoll, setDeleteModalPoll] = useState<Poll | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortValue, setSortValue] = useState<string>("publishDateDesc");
  const [showPollCreatedAlert, setShowPollCreatedAlert] = useState(false);
  const location = useLocation();
  const fetchRequestIdRef = useRef(0);

  // Tab configuration
  const tabs = [
    { label: t("polls.tabs.assigned"), content: null },
    { label: t("polls.tabs.unassigned"), content: null },
    { label: t("polls.tabs.draft"), content: null },
    { label: t("polls.tabs.archive"), content: null },
  ];

  // Map tab index to status
  const getStatusFromTab = (tabIndex: number): PollStatus | undefined => {
    const statusMap: (PollStatus | undefined)[] = [
      "atanmis",
      "atanmamis",
      "taslak",
      "arsiv",
    ];
    return statusMap[tabIndex];
  };

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Eğitim listesi (filtre seçenekleri için)
  useEffect(() => {
    apiClient
      .get<{ data?: AdminEducationItem[] }>("/api/admin/educations", {
        params: { limit: 100, page: 1 },
      })
      .then(({ data }) => {
        const items = Array.isArray(data?.data) ? data.data : [];
        setEducationOptions(
          items
            .filter((e) => e.id)
            .map((e) => ({
              label: e.name ?? e.id,
              value: e.id,
            })),
        );
      })
      .catch(() => setEducationOptions([]));
  }, []);

  // Seçili eğitim ID'lerini normalize et (string | string[] -> string[])
  const selectedEducationIds = useMemo(() => {
    const val = filterValues.educationIds;
    if (Array.isArray(val)) return val.filter((id): id is string => !!id);
    if (typeof val === "string" && val) return [val];
    return undefined;
  }, [filterValues.educationIds]);

  // Sort seçimini API parametrelerine çevir (Swagger: sortBy, sortOrder)
  const sortParams = useMemo((): {
    sortBy?:
      | "name"
      | "publishDate"
      | "participatedPercentage"
      | "remainingTime";
    sortOrder?: "asc" | "desc";
  } => {
    switch (sortValue) {
      case "nameAsc":
        return { sortBy: "name", sortOrder: "asc" };
      case "nameDesc":
        return { sortBy: "name", sortOrder: "desc" };
      case "publishDateDesc":
        return { sortBy: "publishDate", sortOrder: "desc" };
      case "publishDateAsc":
        return { sortBy: "publishDate", sortOrder: "asc" };
      case "participationStatusAsc":
        return { sortBy: "participatedPercentage", sortOrder: "asc" };
      case "participationStatusDesc":
        return { sortBy: "participatedPercentage", sortOrder: "desc" };
      case "remainingTimeDesc":
        return { sortBy: "remainingTime", sortOrder: "desc" };
      case "remainingTimeAsc":
        return { sortBy: "remainingTime", sortOrder: "asc" };

      default:
        return { sortBy: "publishDate", sortOrder: "desc" };
    }
  }, [sortValue]);

  // Fetch polls
  const fetchPolls = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    setIsLoading(true);
    try {
      const status = getStatusFromTab(activeTab);
      const response = await pollsApi.getAll({
        page: currentPage,
        pageSize,
        filters: {
          search: debouncedSearch,
          status,
          educationIds: selectedEducationIds,
          sortBy: sortParams.sortBy,
          sortOrder: sortParams.sortOrder,
        },
      });
      if (requestId !== fetchRequestIdRef.current) return;
      setPolls(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching polls:", error);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    activeTab,
    selectedEducationIds,
    sortParams.sortBy,
    sortParams.sortOrder,
  ]);

  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  // Show success alert when redirected after creating a poll; switch to "Atanmamış" tab
  useEffect(() => {
    const state = location.state as { pollCreatedSuccess?: boolean } | null;
    if (state?.pollCreatedSuccess) {
      setShowPollCreatedAlert(true);
      setActiveTab(1);
      setCurrentPage(1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleRowSelect = (row: Poll) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(polls);
    } else {
      setSelectedRows([]);
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [rowId]: isOpen,
    }));
  };

  const handleAddNew = () => {
    navigate("/dashboard/polls/create");
  };

  // Handle filter change
  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  // Handle tab change
  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCurrentPage(1);
  };

  // Handle delete
  const handleDeleteClick = (poll: Poll) => {
    setDeleteModalPoll(poll);
    handleDropdownToggle(poll.id, false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalPoll) return;
    setIsDeleting(true);
    try {
      await pollsApi.delete(deleteModalPoll.id);
      toast.success(t("polls.deleteSuccess"));
      setDeleteModalPoll(null);
      fetchPolls();
    } catch {
      toast.error(
        t("polls.deleteError"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle archive
  const handleArchiveClick = async (poll: Poll) => {
    try {
      await pollsApi.update(poll.id, { status: SurveyStatus.ARCHIVE });
      toast.success(t("polls.archiveSuccess"));
      fetchPolls();
    } catch {
      toast.error(
        t("polls.archiveError"),
      );
    }
  };

  // Handle unarchive (Arşiv sekmesinde)
  const handleUnarchiveClick = async (poll: Poll) => {
    try {
      await pollsApi.update(poll.id, { status: SurveyStatus.UNASSIGNED });
      toast.success(t("polls.unarchiveSuccess"));
      fetchPolls();
    } catch {
      toast.error(
        t("polls.unarchiveError"),
      );
    }
  };

  // Sort options (Swagger: sortBy = name | publishDate | participatedPercentage | remainingTime, sortOrder = asc | desc)
  const sortOptions: SortOption[] = [
    { label: t("polls.sort.nameAsc"), value: "nameAsc" },
    { label: t("polls.sort.nameDesc"), value: "nameDesc" },
    {
      label: t("polls.sort.publishDateDesc"),
      value: "publishDateDesc",
    },
    {
      label: t("polls.sort.publishDateAsc"),
      value: "publishDateAsc",
    },
    {
      label: t("polls.sort.participationStatusDesc"),
      value: "participationStatusDesc",
    },
    {
      label: t("polls.sort.participationStatusAsc"),
      value: "participationStatusAsc",
    },
    {
      label: t("polls.sort.remainingTimeDesc"),
      value: "remainingTimeDesc",
    },
  ];

  // Handle sort change
  const handleSortChange = (option: SortOption) => {
    setSortValue(option.value ?? "publishDateDesc");
    setCurrentPage(1);
  };

  // Filter fields
  const filterFields: FilterField[] = useMemo(
    () => [
      {
        name: "educationIds",
        label: t("polls.filters.relatedEducation"),
        type: "dropdown",
        placeholder: t("polls.filters.relatedEducationPlaceholder"),
        options: educationOptions,
        searchable: true,
        multiple: true,
      },
    ],
    [t, educationOptions],
  );

  // Table columns
  const columns: TableColumn<Poll>[] = [
    {
      header: t("polls.columns.pollName"),
      accessor: "pollName",
      className: "poll-name-column",
    },
    {
      header: t("polls.columns.type"),
      accessor: (row) => (
        <span>{row.isAnonymous ? t("polls.anonymous.yes") : t("polls.anonymous.no")}</span>
      ),
      className: "poll-type-column",
    },
    {
      header: t("polls.columns.publishDate"),
      accessor: "publishDate",
      className: "publish-date-column",
    },
    {
      header: t("polls.columns.relatedEducation"),
      accessor: "relatedEducation",
      className: "related-education-column",
    },
    {
      header: t("polls.columns.participationStatus"),
      accessor: (row) => (
        <ProgressBar
          value={row.participation.percentage}
          score={row.participatedCount}
          total={row.participation.total}
          size="small"
        />
      ),
      className: "participation-status-column",
    },
    {
      header: t("polls.columns.remainingTime"),
      accessor: "remainingTime",
      className: "remaining-time-column",
    },
  ];

  // Render actions
  const renderActions = (row: Poll) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuItems = [
      {
        icon: Eye,
        label: t("polls.actions.viewDetail"),
        onClick: () => {
          navigate(`/dashboard/polls/${row.id}/detail`);
          handleDropdownToggle(rowId, false);
        },
      },
      {
        icon: Employees,
        label: t("polls.actions.viewAttendees"),
        onClick: () => {
          navigate(`/dashboard/polls/${row.id}/attendees`);
          handleDropdownToggle(rowId, false);
        },
      },
      canUpdate && {
        icon: Edit,
        label: t("polls.actions.edit"),
        color: "#505766",
        onClick: () => {
          navigate(`/dashboard/polls/${row.id}/edit`);
          handleDropdownToggle(rowId, false);
        },
      },
      canUpdate && {
        icon: Document,
        label:
          activeTab === 3
            ? t("polls.actions.unarchive")
            : t("polls.actions.archive"),
        onClick: () => {
          if (activeTab === 3) {
            handleUnarchiveClick(row);
          } else {
            handleArchiveClick(row);
          }
          handleDropdownToggle(rowId, false);
        },
      },
      canDelete && {
        icon: Delete,
        label: t("polls.actions.delete"),
        onClick: () => handleDeleteClick(row),
        color: "#505766",
      },
    ].filter(Boolean) as {
      icon: React.ComponentType<{
        width?: number;
        height?: number;
        className?: string;
        color?: string;
      }>;
      label: string;
      color?: string;
      onClick: () => void;
    }[];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              type="button"
              className={`${styles.actionMenuItem} ${item.color === "error" ? styles.error : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
            >
              {Icon && (
                <Icon
                  width={24}
                  height={24}
                  className={styles.actionMenuIcon}
                  color={item.color === "error" ? "#ef4444" : "#505766"}
                />
              )}
              <span className={styles.actionMenuLabel}>{item.label}</span>
            </button>
          );
        })}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="top"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("polls.title")} />

      {showPollCreatedAlert && (
        <div className={styles.toastWrapper}>
          <Toast
            variant="success"
            title={t("createPoll.publishSuccessTitle")}
            message={t("createPoll.publishSuccessAlert")}
            onClose={() => setShowPollCreatedAlert(false)}
            duration={5000}
          />
        </div>
      )}

      <div className={styles.tabContainer}>
        <TabGroup
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={handleTabChange}
          orientation="horizontal"
        />
      </div>

      <PageHeader
        searchPlaceholder={t("polls.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={sortValue}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? handleAddNew : undefined}
        addButtonLabel={
          canCreate ? t("polls.addButton") : undefined
        }
      />

      <Table
        columns={columns}
        data={polls}
        isLoading={isLoading}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("polls.noData")}
      />

      <ConfirmModal
        isOpen={!!deleteModalPoll}
        onClose={() => setDeleteModalPoll(null)}
        onConfirm={handleConfirmDelete}
        title={t("polls.deleteModal.title")}
        message={
          deleteModalPoll
            ? t(
                "polls.deleteModal.message",
                t("polls.deleteConfirm"),
                {
                  name: deleteModalPoll.pollName,
                },
              )
            : ""
        }
        confirmText={t("polls.deleteModal.confirm")}
        cancelText={t("common.cancel")}
        confirmVariant="danger"
        loading={isDeleting}
      />
    </DashboardLayout>
  );
};

export default PollsPage;
